/*
 * shipping-calculator.js
 * ---------------------------------------------------------------------------
 * "Calcular frete" (CEP) shipping-rate lookup — real rates via Shopify's AJAX
 * Cart API (`GET /cart/shipping_rates.json`), the same technique our old
 * Minimog theme used for its cart shipping estimator. Adapted for this store,
 * which is pt-BR/BRL-only (see CLAUDE.md): the destination country is
 * hardcoded to BR, so there's no country/province picker — just a CEP field.
 *
 * Drives any `[data-shipping-calc]` container: the product-page C6 block
 * (blocks/enfoque-c6.liquid) and the always-on cart page/drawer widget
 * (snippets/enfoque-shipping-calculator.liquid). Document-delegated so it
 * survives Theme-Editor section re-renders and cart-drawer re-fetches.
 *
 * `window.shippingCalculatorStrings` (layout/theme.liquid) supplies the
 * localized copy. Rates come back in cents, formatted with
 * theme.Currency.formatMoney (theme.js, loaded first — see script order).
 *
 * `shipping_rates.json` REQUIRES a province for a BR address — verified
 * against the live store: omitting it 422s with {"province":["Selecione um
 * estado"]}, even though the UI only asks for a CEP. So each lookup resolves
 * the state first via ViaCEP (viacep.com.br — free, keyless, CORS-enabled,
 * the standard Brazilian CEP→address API) and feeds its `uf` in as
 * shipping_address[province] before calling Shopify.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Same glyph as snippets/icon-loading.liquid, inlined because .js assets
  // can't {% render %} a Liquid snippet. `at-icon at-spin` reuses the
  // existing spin keyframe (assets/theme.css.liquid).
  var SPINNER_SVG = '<svg class="at-icon at-spin enfc-frete__spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26.349 26.35"><circle cx="13.792" cy="3.082" r="3.082"/><circle cx="13.792" cy="24.501" r="1.849"/><circle cx="6.219" cy="6.218" r="2.774"/><circle cx="21.365" cy="21.363" r="1.541"/><circle cx="3.082" cy="13.792" r="2.465"/><circle cx="24.501" cy="13.791" r="1.232"/><path d="M4.694,19.84c-0.843,0.843-0.843,2.207,0,3.05c0.842,0.843,2.208,0.843,3.05,0c0.843-0.843,0.843-2.207,0-3.05 C6.902,18.996,5.537,18.988,4.694,19.84z"/><circle cx="21.364" cy="6.218" r="0.924"/></svg>';

  function cepDigits(value) {
    return (value || '').replace(/\D/g, '');
  }

  function strings() {
    return window.shippingCalculatorStrings || {};
  }

  function formatPrice(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    return (window.theme && window.theme.Currency) ? theme.Currency.formatMoney(cents, format) : cents;
  }

  function showMessage(result, text, isError) {
    result.classList.toggle('is-error', !!isError);
    result.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'enfc-frete__msg';
    p.textContent = text;
    result.appendChild(p);
    result.style.display = 'block';
  }

  function showLoading(result) {
    result.classList.remove('is-error');
    result.innerHTML = '<p class="enfc-frete__msg enfc-frete__msg--loading">' + SPINNER_SVG + '<span>' + (strings().calculating || '…') + '</span></p>';
    result.style.display = 'block';
  }

  // Shopify's delivery_range is a same-format [earliest, latest] ISO date
  // pair — for a same-day pickup rate both entries are identical, which
  // read as a confusing duplicated date. Business-day count matches the
  // "5 a 8 dias úteis" copy already used in blocks/enfoque-c6-option.liquid's
  // presets, with the actual date alongside it for people who want it.
  function businessDaysBetween(from, to) {
    var d = new Date(from.getTime());
    var count = 0;
    while (d < to) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  }

  function formatShortDate(date) {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }

  function formatDeliveryRange(range) {
    if (!range || range.length < 2 || !range[0] || !range[1]) return '';
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var start = new Date(range[0] + 'T00:00:00');
    var end = new Date(range[1] + 'T00:00:00');
    if (isNaN(start) || isNaN(end)) return '';

    var sameDate = range[0] === range[1];
    var startDays = businessDaysBetween(today, start);

    if (sameDate && startDays <= 0) return 'Hoje';

    if (sameDate) {
      return (startDays === 1 ? '1 dia útil' : startDays + ' dias úteis') + ' (' + formatShortDate(start) + ')';
    }

    var endDays = businessDaysBetween(today, end);
    return startDays + ' a ' + endDays + ' dias úteis (' + formatShortDate(start) + ' a ' + formatShortDate(end) + ')';
  }

  function showRates(result, rates) {
    result.classList.remove('is-error');
    result.innerHTML = '';
    rates.forEach(function (rate) {
      var row = document.createElement('div');
      row.className = 'enfc-frete__opt';

      var info = document.createElement('div');
      var name = document.createElement('div');
      name.className = 'n';
      name.textContent = rate.name || '';
      info.appendChild(name);
      var rangeText = formatDeliveryRange(rate.delivery_range);
      if (rangeText) {
        var range = document.createElement('div');
        range.className = 'd';
        range.textContent = rangeText;
        info.appendChild(range);
      }

      var price = document.createElement('span');
      price.className = 'p';
      price.innerHTML = formatPrice(rate.price);

      row.appendChild(info);
      row.appendChild(price);
      result.appendChild(row);
    });
    result.style.display = 'block';
  }

  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok) {
          // A rates lookup on a product page with an EMPTY cart 422s — that's not a
          // customer-facing error, it's "the lookup can't run yet". The caller flags it
          // (`ratesError`) and degrades to the labelled estimate instead of a red error.
          var err = new Error('http-' + res.status);
          err.httpError = true;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  // The fallback options (blocks/enfoque-c6.liquid children) are merchant-entered
  // ESTIMATES, not quotes: only ever shown after a lookup ran and produced nothing
  // usable, and only when the merchant actually filled some in — an empty fallback
  // container (label + caption but no `.enfc-frete__opt` row) must never be shown.
  function hasFallbackOptions(fallback) {
    return !!(fallback && fallback.querySelector('.enfc-frete__opt'));
  }

  function showEstimateOrMessage(result, fallback) {
    if (hasFallbackOptions(fallback)) {
      result.style.display = 'none';
      fallback.style.display = 'block';
      return;
    }
    var noCart = fallback && fallback.getAttribute('data-shipping-calc-nocart');
    showMessage(result, noCart || strings().noRate || 'Sem opções de frete para este CEP.', false);
  }

  // Analytics signal (assets/lead-events.js → GA4 `shipping_quote`, not a lead).
  // UF only — never the CEP itself, which is personal data.
  function trackQuote(uf, result, rates) {
    if (typeof window.enfoqueTrack !== 'function') return;
    var data = { uf: uf || '', result: result, rates_count: rates ? rates.length : 0 };
    if (rates && rates.length) {
      data.min_price = Math.min.apply(null, rates.map(function (r) { return parseFloat(r.price) || 0; }));
    }
    window.enfoqueTrack('shipping_quote', data);
  }

  function calculate(root) {
    var input = root.querySelector('[data-shipping-calc-input]');
    var btn = root.querySelector('[data-shipping-calc-btn]');
    var result = root.querySelector('[data-shipping-calc-result]');
    var fallback = root.querySelector('[data-shipping-calc-fallback]');
    if (!input || !result) return;

    var cep = cepDigits(input.value);
    if (fallback) fallback.style.display = 'none';
    // The product-page block's idle hint ("Informe seu CEP…") has done its job once a
    // lookup starts — the result area speaks from here on.
    var hint = root.querySelector('[data-shipping-calc-hint]');
    if (hint) hint.style.display = 'none';

    if (cep.length !== 8) {
      input.focus();
      showMessage(result, strings().error || 'CEP inválido.', true);
      return;
    }

    if (btn) btn.disabled = true;
    showLoading(result);
    var uf = '';

    fetchJSON('https://viacep.com.br/ws/' + cep + '/json/')
      .then(function (address) {
        if (!address || address.erro) {
          var err = new Error('cep-not-found');
          err.notFound = true;
          throw err;
        }
        uf = address.uf || '';
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        var url = cartUrl + '/shipping_rates.json?shipping_address%5Bzip%5D=' +
          encodeURIComponent(cep) + '&shipping_address%5Bcountry%5D=BR&shipping_address%5Bprovince%5D=' +
          encodeURIComponent(address.uf || '');
        return fetchJSON(url).catch(function (err) {
          err.ratesError = true;
          throw err;
        });
      })
      .then(function (data) {
        if (btn) btn.disabled = false;
        if (data && Array.isArray(data.shipping_rates)) {
          if (data.shipping_rates.length > 0) {
            showRates(result, data.shipping_rates);
            trackQuote(uf, 'rates', data.shipping_rates);
          } else {
            showEstimateOrMessage(result, fallback);
            trackQuote(uf, 'no_rates');
          }
        } else {
          showMessage(result, strings().error || 'Não foi possível calcular o frete.', true);
        }
      })
      .catch(function (err) {
        if (btn) btn.disabled = false;
        if (err && err.ratesError) {
          // The CEP resolved fine; Shopify just couldn't price it (typically an empty
          // cart on a product page). Show the labelled estimate, or say so plainly.
          showEstimateOrMessage(result, fallback);
          trackQuote(uf, 'estimate');
          return;
        }
        var message = (err && err.notFound) ? (strings().cepNotFound || 'CEP não encontrado.') : (strings().error || 'Não foi possível calcular o frete.');
        showMessage(result, message, true);
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-shipping-calc-btn]');
    if (!btn) return;
    e.preventDefault();
    var root = btn.closest('[data-shipping-calc]');
    if (root) calculate(root);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-shipping-calc-input]');
    if (!input) return;
    e.preventDefault();
    var root = input.closest('[data-shipping-calc]');
    if (root) calculate(root);
  });
})();
